import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, ArrowRight, RefreshCw } from 'lucide-react';
import { createHealthStory } from '@/utils/healthStoryGenerator';
import { applyPersonality } from '@/utils/aiPersonality';

/**
 * Health Story Component
 * Displays user's weekly health patterns as a simple, connected narrative
 * Can be used as standalone page or widget in Dashboard
 */

const HealthStory = ({ asWidget = false }) => {
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHealthStory();
  }, []);

  const loadHealthStory = () => {
    setLoading(true);
    
    // Simulate loading
    setTimeout(() => {
      // In production, this would fetch real data from backend
      // For now, using mock data generator
      const generatedStory = createHealthStory();
      setStory(generatedStory);
      setLoading(false);
    }, 800);
  };

  const handleRefresh = () => {
    loadHealthStory();
  };

  if (loading) {
    return (
      <Card className={`border-2 ${asWidget ? 'border-purple-200' : 'border-purple-300'} shadow-lg`}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">
            Analyzing your health patterns...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!story) {
    return (
      <Card className="border-2 border-gray-200">
        <CardContent className="pt-6 text-center py-8">
          <p className="text-gray-600">
            No health story available yet. Start tracking to see your story unfold.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Color scheme based on story type
  const colorScheme = {
    positive: {
      bg: 'from-green-50 to-emerald-50',
      border: 'border-green-300',
      text: 'text-green-900',
      accent: 'text-green-600',
      button: 'bg-green-600 hover:bg-green-700'
    },
    correlation: {
      bg: 'from-blue-50 to-cyan-50',
      border: 'border-blue-300',
      text: 'text-blue-900',
      accent: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700'
    },
    encouraging: {
      bg: 'from-purple-50 to-pink-50',
      border: 'border-purple-300',
      text: 'text-purple-900',
      accent: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700'
    },
    building: {
      bg: 'from-amber-50 to-orange-50',
      border: 'border-amber-300',
      text: 'text-amber-900',
      accent: 'text-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700'
    },
    new_user: {
      bg: 'from-gray-50 to-slate-50',
      border: 'border-gray-300',
      text: 'text-gray-900',
      accent: 'text-gray-600',
      button: 'bg-gray-600 hover:bg-gray-700'
    },
    neutral: {
      bg: 'from-indigo-50 to-purple-50',
      border: 'border-indigo-300',
      text: 'text-indigo-900',
      accent: 'text-indigo-600',
      button: 'bg-indigo-600 hover:bg-indigo-700'
    }
  };

  const colors = colorScheme[story.type] || colorScheme.neutral;

  // Widget version (compact)
  if (asWidget) {
    return (
      <Card className={`border-2 ${colors.border} bg-gradient-to-br ${colors.bg} shadow-lg`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-600" />
              <span className="text-lg">Your Health Story</span>
            </div>
            <span className="text-3xl">{story.emoji}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={`${colors.text} text-base leading-relaxed mb-4`}>
            {story.story}
          </p>
          
          {story.actionable && (
            <div className="bg-white/70 rounded-lg p-3 border border-current/20">
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-800">
                  <strong>Try this:</strong> {story.actionable}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full page version
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your Health Story
          </h1>
          <p className="text-gray-600">
            Your week in simple, connected insights
          </p>
        </div>

        {/* Main Story Card */}
        <Card className={`border-2 ${colors.border} bg-gradient-to-br ${colors.bg} shadow-xl`}>
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <span className="text-6xl">{story.emoji}</span>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <p className={`${colors.text} text-xl leading-relaxed text-center mb-6`}>
                {story.story}
              </p>
              
              {story.actionable && (
                <div className="bg-white rounded-lg p-5 border-2 border-current/20 shadow-md">
                  <div className="flex items-start gap-3">
                    <div className={`${colors.button} rounded-full p-2 flex-shrink-0`}>
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        What you can do next:
                      </h3>
                      <p className="text-gray-800">
                        {story.actionable}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Supporting Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900">
                <strong>About Your Health Story:</strong> This summary is generated from your 
                daily check-ins over the past 7 days. The more you track, the clearer your 
                story becomes. Patterns help you understand what works for your body.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="border-2 border-purple-300 hover:bg-purple-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Story
          </Button>
          
          <Button
            onClick={() => navigate('/progress')}
            variant="outline"
            className="border-2 border-blue-300 hover:bg-blue-50"
          >
            View Detailed Patterns
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          
          <Button
            onClick={() => navigate('/daily-loop')}
            className={`${colors.button} text-white`}
          >
            Track Today
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Back Button */}
        <div className="flex justify-center">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-2 border-gray-300 hover:bg-gray-50"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Widget version for embedding in Dashboard
 */
export const HealthStoryWidget = () => {
  return <HealthStory asWidget={true} />;
};

export default HealthStory;
